import apiClient from "./apiClient";
import { isAuthenticated } from "./auth";

const CACHE_DURATION = 3 * 60 * 1000; // 3 minutos en milisegundos

const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  const now = Date.now();
  const lastUpdate = parseInt(timestamp);
  return (now - lastUpdate) < CACHE_DURATION;
};

const invalidateProductsCache = () => {
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
  // También invalidar caché de admin
  localStorage.removeItem('admin_products');
  localStorage.removeItem('admin_productsTimestamp');
};

const productRequestMessage = (error, fallback) => {
  const data = error.response?.data;
  const detail = data?.error || data?.message || data?.detail || data?.blisters;
  const first = Array.isArray(detail) ? detail[0] : detail;
  return `${fallback}${first ? `: ${typeof first === 'string' ? first : JSON.stringify(first)}` : ''}`;
};

// Sugerencias del buscador global: consultar el catálogo, no la página guardada en caché.
export const searchProducts = async (query, limit = 5) => {
  const params = { search: query, page: 1, page_size: limit, is_visible: true };
  const response = await apiClient.get('/products/', { params });
  return Array.isArray(response.data) ? response.data : (response.data.results || []);
};


// Nueva función para obtener productos con paginación
export const getProductsPaginated = async (page = 1, pageSize = 9, filters = {}) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Construir URL con parámetros de paginación y filtros
    let url = `/products/?page=${page}&page_size=${pageSize}&is_visible=true`;
    
    // Agregar filtros si existen
    if (filters.type && filters.type.length > 0) {
      url += `&type=${filters.type.join(',')}`;
    }
    if (filters.category && filters.category.length > 0) {
      url += `&category=${encodeURIComponent(filters.category.join(','))}`;
    }
    if (filters.catalogCategories && filters.catalogCategories.length > 0) {
      url += `&catalog_categories=${encodeURIComponent(filters.catalogCategories.join(','))}`;
    }
    if (filters.excludeTypes && filters.excludeTypes.length > 0) {
      url += `&exclude_types=${filters.excludeTypes.join(',')}`;
    }
    if (filters.brands && filters.brands.length > 0) {
      url += `&brand=${filters.brands.join(',')}`;
      console.log('Adding brand filter to URL:', filters.brands);
    }
    if (filters.search) {
      url += `&search=${encodeURIComponent(filters.search)}`;
    }
    if (filters.onlyDiscounted) {
      url += `&discount=true`;
    }
    if (filters.priceRange && filters.priceRange.length > 0) {
      url += `&priceRange=${filters.priceRange.join(',')}`;
    }
    if (filters.stockFilter && filters.stockFilter.length > 0) {
      url += `&stockFilter=${filters.stockFilter.join(',')}`;
    }
    if (filters.noStock) {
      url += `&no_stock=true`;
    }
    if (filters.ordering) {
      url += `&ordering=${filters.ordering}`;
    }
    
    // Add highlighted filter if specified (for homepage carousel)
    if (filters.highlighted === true) {
      url += `&highlighted=true`;
      console.log('Adding highlighted filter for homepage carousel products');
    }
    
    // Add limited edition filter if specified (for catalog filtering)
    if (filters.limitedEdition === true) {
      url += `&limitedEdition=true`;
      console.log('Adding limitedEdition filter for limited edition products');
    }
    
    console.log('Fetching products with URL:', url);
    console.log('Filters applied:', filters);
    const response = await apiClient.get(url, { headers });
    
    console.log('API Response data:', response.data);
    console.log('API Response data structure:', Object.keys(response.data));
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      // Standard paginated response format
      result = {
        ...response.data,
        // Ensure count is always a number, defaulting to the length of results if missing
        count: typeof response.data.count === 'number' ? response.data.count : 
               (response.data.results ? response.data.results.length : 0)
      };
      console.log('Using paginated response format. Count:', result.count);
    } else if (Array.isArray(response.data)) {
      // Handle case where API returns array instead of object with results
      result.results = response.data;
      result.count = response.data.length;
      console.log('Using array response format. Calculated count:', result.count);
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching paginated products:', error);
    throw error;
  }
};

export const getProducts = async (page = 1, pageSize = 20) => {
  try {
    const isAuth = isAuthenticated();

    console.log(`Obteniendo productos página ${page}`);
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}&is_visible=true`;
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Función específica para admin que devuelve productos paginados sin cargar todo
export const getAllProductsForAdmin = async (ignoreCache = false, page = 1, pageSize = 20, filters = {}) => {
  try {
    const cachedData = localStorage.getItem('admin_products');
    const cacheTimestamp = localStorage.getItem('admin_productsTimestamp');
    
    // Solo usar caché para la primera página y sin filtros
    const hasFilters = Object.values(filters).some(value => value && value.length > 0);
    if (!ignoreCache && page === 1 && !hasFilters && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      console.log('Usando caché de productos admin');
      const products = JSON.parse(cachedData);
      return { results: products, count: products.length, next: null, previous: null };
    }

    console.log(`Obteniendo productos página ${page} para admin con filtros:`, filters);
    const token = localStorage.getItem("accessToken");
    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Agregar filtros a la URL
    if (filters.search) {
      url += `&search=${encodeURIComponent(filters.search)}`;
    }
    if (filters.brand) {
      url += `&brand=${encodeURIComponent(filters.brand)}`;
    }
    if (filters.type) {
      url += `&type=${encodeURIComponent(filters.type)}`;
    }
    if (filters.category) {
      url += `&category=${encodeURIComponent(filters.category)}`;
    }
    if (filters.visibility) {
      if (filters.visibility === 'visible') {
        url += `&is_visible=true`;
      } else if (filters.visibility === 'hidden') {
        url += `&is_visible=false`;
      }
    }
    if (filters.stockFilter) {
      url += `&stockFilter=${encodeURIComponent(filters.stockFilter)}`;
    }
    if (filters.priceStatus) {
      url += `&priceStatus=${encodeURIComponent(filters.priceStatus)}`;
    }
    if (filters.noStock) {
      url += `&no_stock=true`;
    }
    // Add limitedEdition filter if specified
    if (filters.limitedEdition !== null && filters.limitedEdition !== undefined) {
      url += `&limitedEdition=${filters.limitedEdition}`;
      console.log('Adding limitedEdition filter:', filters.limitedEdition);
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    console.log(`Productos obtenidos para admin página ${page}: ${result.results.length}`);
    
    // Solo guardar en caché la primera página sin filtros
    if (page === 1 && !hasFilters) {
      localStorage.setItem('admin_products', JSON.stringify(result.results));
      localStorage.setItem('admin_productsTimestamp', Date.now().toString());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching products for admin:', error);
    throw error;
  }
};

// Función para subir imagen a Cloudinary
export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('upload_preset', 'semillas');
    formData.append('cloud_name', 'djp6rnen1');
    formData.append('file', file);

    const response = await fetch(
      'https://api.cloudinary.com/v1_1/djp6rnen1/image/upload',
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    throw new Error('Error al subir la imagen: ' + (error.message || 'Error desconocido'));
  }
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/');
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

// Función para obtener productos destacados (highlighted)
export const getHighlightedProducts = async (limit = 3) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/highlighted/?limit=${limit}`, { headers });
    
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth && response.data.results) {
      response.data.results = response.data.results.map(product => {
        if (product.blisters) {
          product.blisters = product.blisters.map(blister => {
            return {
              ...blister,
              price: null,
              discount: blister.discount
            };
          });
        }
        return product;
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching highlighted products:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

// Nueva función para obtener productos más recientes con stock y precio
export const getLatestProductsWithStock = async (limit = 8) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Endpoint para obtener productos más recientes con stock y precio
    let url = `/products/?ordering=-created_at&has_stock=true&has_price=true&page_size=${limit}&is_visible=true`;
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching latest products with stock:', error);
    return { results: [] };
  }
};

export const getProduct = async (id) => {
  try {
    console.log('Getting product with ID:', id);
    
    // Incluir token de autenticación si el usuario está autenticado
    const headers = {};
    if (isAuthenticated()) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/${id}/`, { headers });
    console.log('Product API response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error in getProduct:', error);
    console.error('Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al obtener el producto');
  }
};

// Función para crear un nuevo producto
export const createProduct = async (productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Creando producto:', productData);
    const response = await apiClient.post("/products/", productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(productRequestMessage(error, 'Error al crear el producto'));
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Actualizando producto:', productId, productData);
    const response = await apiClient.put(`/products/${productId}/`, productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(productRequestMessage(error, 'Error al actualizar el producto'));
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al eliminar el producto');
  }
};

export const getBlisterStock = async (blisterId) => {
  try {
    const response = await apiClient.get(`/products/blisters/${blisterId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blister stock:', error);
    throw error;
  }
};

// La visibilidad en la tienda es independiente del stock y de `available`.
export const toggleProductVisibility = async (productId, isVisible) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.patch(`/products/${productId}/`, { is_visible: isVisible }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al cambiar la visibilidad del producto');
  }
};

// Reordenar productos (drag & drop / flechas) — solo admin
export const updateProductsOrder = async (items) => {
  try {
    const response = await apiClient.patch('/products/update_order/', { items });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Error al reordenar los productos');
  }
};

// Función para cambiar el estado destacado de un producto (solo admin)
export const toggleProductHighlighted = async (productId, highlighted) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando estado destacado del producto:', productId, 'a:', highlighted);
    // Ensure we're sending a boolean value, not a string
    const highlightedBool = highlighted === true || highlighted === 'true';
    const response = await apiClient.patch(`/products/${productId}/`, { highlighted: highlightedBool }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al cambiar el estado destacado del producto');
  }
};

// Función para cambiar el estado de edición limitada de un producto (solo admin)
export const toggleProductLimitedEdition = async (productId, limitedEdition) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando estado de edición limitada del producto:', productId, 'a:', limitedEdition);
    // Ensure we're sending a boolean value, not a string
    const limitedEditionBool = limitedEdition === true || limitedEdition === 'true';
    const response = await apiClient.patch(`/products/${productId}/`, { limited_edition: limitedEditionBool }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al cambiar el estado de edición limitada del producto');
  }
};
